import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { WASHUB_CONSTANTS } from '../../../shared/Constants';
import { useAPI } from '../../../shared/services/api/API';
import { PackagingLabelOrderDetails } from '../model/makeData';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
declare const qz: any;

export const GarmentPackagingLabel = () => {
  const navigate = useNavigate();
  const { httpGet, httpPost, handleAlertBar } = useAPI();
  const [businessStoreId, setBusinessStoreId] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [businessStoreData, setBusinessStoreData] = useState<any>([]);
  const [departmentId, setDepartmentId] = useState('');
  const [departmentList, setDepartmentList] = useState<any>([]);
  const [orderId, setOrderId] = useState('');
  const [tableData, setTableData] = useState<PackagingLabelOrderDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [printLabelPayload, SetPrintLabelPayload] = useState({
    orderId: '',
    packedBy: '',
  });
  const queryParams = {
    page: 0,
    size: 10000,
    sortColumn: 'modifiedAt',
    isDescending: true,
  };

  // GET with API : BUSINESSSTORE
  const fetchBusinessStore = async () => {
    httpGet(
      `${WASHUB_CONSTANTS.API_URLS.BUSINESS_STORE_ADMIN_BUSINESS_DATA_GET}`,
      queryParams,
    ).then((response) => {
      if (response.status === 200) {
        const sortBusinessStoreArray = response?.data?.sort((a: any, b: any) =>
          a?.name.toLowerCase().localeCompare(b.name.toLowerCase()),
        );
        setBusinessStoreData(sortBusinessStoreArray);
      } else {
        if (response.data.error) {
          console.error(
            'Error fetching BusinessStore: ',
            response.data.message,
          );
        }
      }
    });
  };

  // GET with API : BUSINESSSTORE
  const fetchDepartmentStore = async (busStoreId: string) => {
    httpGet(
      `${WASHUB_CONSTANTS.API_URLS.BUSINESSSTORE_DEPARTMENT_DATA_GET}/${busStoreId}`,
      queryParams,
    ).then((response) => {
      if (response.status === 200) {
        const sortBusinessDeptArray = response?.data?.sort((a: any, b: any) =>
          a?.name.toLowerCase().localeCompare(b.name.toLowerCase()),
        );
        setDepartmentList(sortBusinessDeptArray);
      } else {
        if (response.data.error) {
          console.error(
            'Error fetching BusinessStore: ',
            response.data.message,
          );
        }
      }
    });
  };

  // GET with API
  const fetchGarmentPackagingLabel = async () => {
    setIsLoading(true);
    httpGet(
      `${WASHUB_CONSTANTS.API_URLS.BUSINESSORDER_UNVERIFIED_GET}/${businessId}/${businessStoreId}/${departmentId}`,
    ).then((response) => {
      if (response.status === 200) {
        setTableData((): any => response?.data);
        setIsLoading(false);
      } else {
        if (response.data.error) {
          console.error('Error fetching serviceitem: ', response.data.message);
          setTableData([]);
          setIsLoading(false);
        }
      }
    });
  };

  // Search Business order by orderId
  const fetchOrdersById = useMemo(
    () =>
      debounce((orderId: string) => {
        httpPost(
          WASHUB_CONSTANTS.API_URLS.SEARCH_BUSINESSORDER_BY_ORDERID_POST,
          orderId,
        ).then((response) => {
          if (response.status === 200) {
            setTableData(response.data);
          } else {
            if (response.data.error) {
              handleAlertBar('error', 'Please Select business');
            }
          }
        });
      }, 700),
    [],
  );

  const columns = useMemo<MRT_ColumnDef<PackagingLabelOrderDetails>[]>(
    () => [
      {
        accessorKey: 'orderId',
        header: 'Order Id',
        size: 10,
      },
      {
        accessorKey: 'orderStatus',
        header: 'Item Status',
        size: 50,
      },
      {
        accessorKey: 'orderDate',
        header: 'Order Date',
        size: 10,
      },
      {
        accessorKey: 'totalItemCount',
        header: 'Item Count',
        size: 10,
      },
      {
        accessorKey: 'serviceType.name',
        header: 'Service Type',
        size: 10,
      },
    ],
    [],
  );

  const handleOpenPrintDialog = (row: any) => {
    setIsPrintDialogOpen(true);
    const reqBody = {
      orderId: row.orderId,
      packedBy: '',
    };
    SetPrintLabelPayload(reqBody);
  };

  const printLabel = async (printerName: string, printerLabel: any) => {
    try {
      if (!qz.websocket.isActive()) {
        await qz.websocket.connect();
      }
      let printer = "";
      //If printerName is null, it will use default printer
      if (!printerName) {
        printer = await qz.printers.getDefault();
      } else {
        printer = await qz.printers.find(printerName);
      }
      const config = qz.configs.create(printer);
      for (const label of printerLabel) {
        let data = [];
        if (printerName === "Epson") {
          data = [
            {
              type: "raw",
              format: "command",
              flavor: "base64",
              data: label,
            },
          ];
        } else {
          data = [label];
        }
        await qz.print(config, data);
      }
      handleAlertBar("success", "Labels print successfully");
      closePrintDialog();
    } catch (error) {
      alert("Error: " + error);
    }
  };

  const handlePrintLabel = () => {
    httpPost(
      `${WASHUB_CONSTANTS.API_URLS.GARMENT_PACKAGING_PRINTPACKAGE_POST}`,
      printLabelPayload,
    ).then(async (response) => {
      if (response.status === 200) {
        try {
          printLabel('', response?.data?.label)
        } catch (error: any) {
          throw new Error('Error fetching Print label: ', error);
        }
      } else {
        if (response.data.error) {
          console.error('Error fetching Print label: ', response.data.message);
        }
      }
    });
  };

  const closePrintDialog = () => {
    setIsPrintDialogOpen(false);
  };

  const handlePrintLabelDialog = (e: any) => {
    const { name, value } = e.target;
    const newFormData = {
      ...printLabelPayload,
      [name]: name === 'itemCondition' || name === 'packedBy' ? value : +value,
    };
    SetPrintLabelPayload(newFormData);
  };

  useEffect(() => {
    fetchBusinessStore();
  }, []);

  useEffect(() => {
    if (businessStoreId) {
      const businessId = businessStoreData?.find(
        (item: any) => item.id === businessStoreId,
      )?.business?.id;
      setBusinessId(businessId);
      fetchDepartmentStore(businessStoreId);
    }
  }, [businessStoreId]);

  useEffect(() => {
    if (departmentId) {
      fetchGarmentPackagingLabel();
    }
  }, [departmentId]);

  const refetch = () => {
    setOrderId('');
    if (departmentId) {
      fetchGarmentPackagingLabel();
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <Grid container justifyContent={'space-between'}>
        <Grid item className="mb-1 mt-1 ms-3">
          <Typography
            color={'primary'}
            sx={{ textAlign: 'center' }}
            variant="h5"
            className="mt-0"
          >
            Garment Packaging Label Management
          </Typography>
        </Grid>
      </Grid>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          padding: '2px 15px',
          minWidth: { xs: '300px', sm: '360px', md: '400px' },
          gap: '1rem',
        }}
      >
        <TextField
          size="small"
          style={{ width: '30%' }}
          key="businessStoreId"
          label="Select Business Store"
          name="businessStoreId"
          select
          onChange={(e) => {
            setBusinessStoreId(e.target.value);
          }}
          value={businessStoreId}
        >
          {businessStoreData.map((option: any) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          style={{ width: '30%' }}
          key="departmentId"
          label="Select Business Department"
          name="departmentId"
          select
          onChange={(e) => {
            setDepartmentId(e.target.value);
          }}
          value={departmentId}
        >
          {departmentList.map((option: any) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          key="orderId"
          className="me-3"
          style={{ width: '30%' }}
          label="Search Order Id"
          name="orderId"
          onChange={(e) => {
            setOrderId(e.target.value);
            fetchOrdersById(e.target.value);
          }}
          variant="outlined"
          value={orderId}
          InputProps={{
            endAdornment: orderId && (
              <InputAdornment position="end">
                <Link component="button" title={'Clear'}>
                  <CloseIcon
                    fontSize="small"
                    onClick={() => {
                      setOrderId('');
                      refetch();
                    }}
                  />
                </Link>
              </InputAdornment>
            ),
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
        ></TextField>
      </Stack>
      <div style={{ padding: '5px 15px' }}>
        <MaterialReactTable
          defaultColumn={{
            maxSize: 150,
            minSize: 10,
            size: 50,
          }}
          displayColumnDefOptions={{
            'mrt-row-actions': {
              muiTableHeadCellProps: {
                align: 'center',
              },
              size: 100,
            },
          }}
          muiTablePaperProps={{
            sx: {
              width: '100%',
              '& .css-1tbggly': {
                minHeight: '2px',
              },
            },
          }}
          columns={columns}
          enableFullScreenToggle={false}
          enableStickyHeader
          positionActionsColumn="last"
          enableRowActions
          enableColumnActions={false}
          enableColumnFilters={false}
          enableFilters={true}
          enableSorting={false}
          enableHiding={false}
          enableDensityToggle={false}
          enableToolbarInternalActions={false}
          data={tableData}
          state={{ isLoading: isLoading }}
          editDisplayMode="modal"
          muiTableContainerProps={{
            sx: {
              height: '63vh',
            },
          }}
          renderRowActions={({ row, table }) => (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
              <Button
                className="ps-1 pe-1"
                variant="contained"
                sx={{ color: 'white' }}
                onClick={() => {
                  navigate('order-details', {
                    state: {
                      order: row?.original,
                    },
                  });
                }}
              >
                <Typography variant="labelNormal">Details</Typography>
              </Button>
              <Button
                className="ps-1 pe-1"
                variant="contained"
                sx={{ color: 'white' }}
                onClick={() => handleOpenPrintDialog(row.original)}
              >
                <Typography variant="labelNormal">Print Label</Typography>
              </Button>
            </Box>
          )}
        />
      </div>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={isPrintDialogOpen}
        onClose={closePrintDialog}
      >
        <DialogTitle textAlign="center">Print label Details</DialogTitle>
        <DialogContent>
          <div className="d-flex item-center justify-content-between mt-2 ">
            <TextField
              fullWidth
              className="mx-2"
              id="outlined-helperText"
              key="packedBy"
              required
              label="Packed by:"
              name="packedBy"
              type="text"
              size="small"
              multiline
              rows={1}
              value={printLabelPayload.packedBy}
              onChange={(e) => handlePrintLabelDialog(e)}
            />
          </div>
        </DialogContent>
        <DialogActions className="dialog-btn">
          <Button
            color="error"
            variant="contained"
            sx={{ color: 'white' }}
            onClick={closePrintDialog}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePrintLabel}
            sx={{ color: 'white' }}
            variant="contained"
            disabled={printLabelPayload.packedBy === ''}
          >
            Print Label
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
